import { z } from 'zod';

// Cars, as Lagos Life's 🚗 Cars (read from its app and public client code on 21 October 2026): a showroom on
// the map sells everything from a scooter to a hypercar; buy one there (trade the old one in), keep up to ten in
// the garage, choose which you drive, and "Car" joins the travel sheet: fuel only, and quicker than the bus.
// A driver on the Staff payroll takes the wheel: no fuel, quicker still. Cars wear with the miles; a service at
// the showroom puts them right. Selling gives back a share of the price. All of it is content the admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
export const CarModel = z.object({
  id,
  // the maker's name, and what Londoners call it
  label: z.string().trim().min(2).max(40),
  nick: z.string().trim().min(2).max(30),
  emoji: z.string().trim().min(1).max(16),
  // its paint, for the ride (a hex colour)
  colour: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  blurb: z.string().trim().min(10).max(200),
  pricePence: z.number().int().min(100).max(100_000_000_000),
  // what a trip across the map costs in fuel
  fuelPence: z.number().int().min(0).max(1_000_000),
  // a motorbike or scooter (quicker through traffic, but a driver will not take it)
  bike: z.boolean(),
  // a head-turner: arriving in it cheers you up
  flex: z.boolean(),
  visible: z.boolean(),
});
export type CarModel = z.infer<typeof CarModel>;

export const CarsContent = z.object({
  visible: z.boolean(),
  // the showroom on the map: cars are bought, sold and serviced there
  showroomPlaceId: z.string().regex(/^[a-z0-9_]{2,40}$/),
  garageMax: z.number().int().min(1).max(50),
  // the share of the price a car sells back for (and counts for in a trade-in)
  resaleShare: z.number().min(0).max(1),
  // the drive: real seconds across the map, and what it does to the needs
  driveSeconds: z.number().int().min(3).max(600),
  driveFun: z.number().int().min(-20).max(20),
  flexFun: z.number().int().min(0).max(30),
  // with a driver on the payroll the trip takes this share of the time
  driverSpeed: z.number().min(0.2).max(1),
  // wear: condition (100 is showroom fresh) falls this much a week and this much a trip; below `tiredBelow`
  // the car is slower and may not start; a service costs up to this share of the price (all of it when the
  // condition is at 0, in proportion above), at least the minimum
  wearWeekly: z.number().min(0).max(50),
  wearTrip: z.number().min(0).max(20),
  tiredBelow: z.number().int().min(0).max(100),
  serviceShare: z.number().min(0).max(0.2),
  serviceMinPence: z.number().int().min(0).max(1_000_000),
  models: z.array(CarModel).min(1).max(60),
});
export type CarsContent = z.infer<typeof CarsContent>;

const model = (
  id: string,
  label: string,
  nick: string,
  emoji: string,
  colour: string,
  pricePounds: number,
  fuelPence: number,
  blurb: string,
  flags: { bike?: boolean; flex?: boolean } = {},
): CarModel => ({
  id,
  label,
  nick,
  emoji,
  colour,
  blurb,
  pricePence: pricePounds * 100,
  fuelPence,
  bike: flags.bike ?? false,
  flex: flags.flex ?? false,
  visible: true,
});
export const DEFAULT_CARS: CarsContent = {
  visible: true,
  showroomPlaceId: 'showroom',
  garageMax: 10,
  resaleShare: 0.6,
  driveSeconds: 7,
  driveFun: 2,
  flexFun: 4,
  driverSpeed: 0.75,
  wearWeekly: 1,
  wearTrip: 0.5,
  tiredBelow: 40,
  serviceShare: 0.03,
  serviceMinPence: 4_000,
  models: [
    model(
      'pcx',
      'Honda PCX 125',
      'The Scooter',
      '🛵',
      '#2b2f36',
      2_600,
      60,
      'Filters through the traffic and parks anywhere. Your coat will get wet.',
      {
        bike: true,
      },
    ),
    model(
      'corsa',
      'Vauxhall Corsa 2012',
      'The Runaround',
      '🚗',
      '#9aa3ad',
      2_800,
      180,
      'One careful owner, three careless ones. Starts most mornings.',
    ),
    model(
      'fiesta',
      'Ford Fiesta',
      'The Fiesta',
      '🚗',
      '#1f3f6b',
      6_500,
      190,
      "Britain's favourite for a reason: cheap to run and nippy round the back streets.",
    ),
    model(
      'prius',
      'Toyota Prius',
      'The Uber',
      '🚗',
      '#c8c3b5',
      11_000,
      90,
      'Silent, frugal, and every minicab driver in London swears by it.',
    ),
    model(
      'golf',
      'Volkswagen Golf',
      'The Golf',
      '🚗',
      '#f2f2f0',
      16_000,
      200,
      'Does everything well and shows off about none of it.',
    ),
    model(
      'mini',
      'Mini Cooper',
      'The Mini',
      '🚗',
      '#c8102e',
      24_000,
      210,
      'Union Jack on the roof, go-kart round the corners. Made in Oxford.',
    ),
    model(
      'qashqai',
      'Nissan Qashqai',
      'The School Run',
      '🚙',
      '#5c6b4a',
      26_000,
      240,
      'Seven seats and a boot for the big shop. Built in Sunderland.',
    ),
    model(
      'tesla',
      'Tesla Model 3',
      'The EV',
      '🚗',
      '#f2f2f0',
      40_000,
      70,
      'Charges at the kerb, pulls away in silence, and the Congestion Charge waves it through.',
    ),
    model(
      'bmw3',
      'BMW 3 Series',
      'The Rep Car',
      '🚘',
      '#1c2a3a',
      42_000,
      300,
      'Sporty, quiet and leased by half of Canary Wharf.',
      { flex: true },
    ),
    model(
      'eclass',
      'Mercedes E-Class',
      'The Chauffeur',
      '🚘',
      '#2b2f36',
      60_000,
      320,
      'Leather, air suspension and a badge that gets you parked at the front.',
      { flex: true },
    ),
    model(
      'defender',
      'Land Rover Defender',
      'The Defender',
      '🚙',
      '#4f6b4a',
      68_000,
      420,
      'Built for the Highlands, driven in Chelsea. Mud optional.',
      { flex: true },
    ),
    model(
      'porsche',
      'Porsche 911',
      'The Nine Eleven',
      '🏎️',
      '#c8102e',
      105_000,
      450,
      'Flat-six behind you, Embankment in front of you. The rich man’s everyday car.',
      { flex: true },
    ),
    model(
      'rangerover',
      'Range Rover Autobiography',
      'The Range',
      '🚙',
      '#1f3f6b',
      150_000,
      520,
      "Kensington's favourite. Every doorman knows the sound of this door closing.",
      { flex: true },
    ),
    model(
      'g63',
      'Mercedes-AMG G63',
      'The G-Wagon',
      '🖤',
      '#111111',
      180_000,
      620,
      'A box on wheels that stops traffic on Sloane Street.',
      { flex: true },
    ),
    model(
      'bentayga',
      'Bentley Bentayga',
      'Old Money',
      '🚘',
      '#2f4a3a',
      200_000,
      650,
      'Hand-stitched in Crewe. Nobody beeps at a Bentley.',
      { flex: true },
    ),
    model(
      'urus',
      'Lamborghini Urus',
      'The Raging Bull',
      '🏎️',
      '#3cb043',
      220_000,
      700,
      'Bright green, very loud, and the whole of Mayfair turns to look.',
      { flex: true },
    ),
    model(
      'mclaren',
      'McLaren 750S',
      'The Woking Rocket',
      '🏎️',
      '#f26b1d',
      250_000,
      760,
      'Built down the road in Woking. Dihedral doors, and a boot the size of a lunchbox.',
      { flex: true },
    ),
    model(
      'cullinan',
      'Rolls-Royce Cullinan',
      'The Rolls',
      '🚘',
      '#f4f4f2',
      350_000,
      800,
      'Stars in the roof, umbrellas in the doors. You don’t drive it, you arrive in it.',
      { flex: true },
    ),
    model(
      'sf90',
      'Ferrari SF90 Stradale',
      'The Prancing Horse',
      '🏎️',
      '#d7162c',
      400_000,
      900,
      'Rosso Corsa and a thousand horses. Park Lane hears you coming from Hyde Park Corner.',
      { flex: true },
    ),
    model(
      'chiron',
      'Bugatti Chiron',
      'The Unbuyable',
      '🏎️',
      '#1a3a8f',
      3_000_000,
      1_500,
      'Fifteen hundred horses and maybe three of them in Britain. London has never seen one standing still.',
      { flex: true },
    ),
  ],
};
export const carsOf = (content: { cars?: CarsContent }) => content.cars ?? DEFAULT_CARS;
export function carsProblems(cars: CarsContent): string[] {
  const ids = new Set(cars.models.map((item) => item.id));
  return ids.size === cars.models.length ? [] : ['Cars: two models have the same id.'];
}

// the money back on a sale (or allowed on a trade-in)
export const carResalePence = (cars: CarsContent, model: CarModel) => Math.round(model.pricePence * cars.resaleShare);
// a service: the points missing, priced by the car; nothing when it is as good as new
export function carServicePence(cars: CarsContent, model: CarModel, condition: number) {
  if (condition >= 99.5) return 0;
  const bill = ((100 - condition) / 100) * model.pricePence * cars.serviceShare;
  return Math.max(cars.serviceMinPence, Math.round(bill / 100) * 100);
}
// how worn a car is after the weeks since it was last looked at
export const wornBy = (cars: CarsContent, condition: number, weeks: number, trips: number) =>
  Math.max(0, Math.min(100, condition - cars.wearWeekly * weeks - cars.wearTrip * trips));

// ---------- over the wire ----------

export const CarView = z.object({
  id: z.string().uuid(),
  model: z.string(),
  boughtAt: z.string(),
  condition: z.number(),
  // the one taken out when "Car" is chosen on the travel sheet
  driving: z.boolean(),
  resalePence: z.number().int(),
  servicePence: z.number().int(),
});
export type CarView = z.infer<typeof CarView>;
export const CarOfferView = z.object({
  model: z.string(),
  pricePence: z.number().int(),
  // what buying it costs after the car being driven is traded in (null: no trade-in)
  tradeInPence: z.number().int().nullable(),
  // why it cannot be bought now (null when it can)
  blocked: z.string().nullable(),
});
export type CarOfferView = z.infer<typeof CarOfferView>;
export const CarsReply = z.object({
  cars: z.array(CarView),
  showroom: z.array(CarOfferView),
  // the showroom place, whether the player is there now, and how to get there
  showroomPlaceId: z.string(),
  showroomName: z.string(),
  atShowroom: z.boolean(),
  garageMax: z.number().int(),
  walletPence: z.number().int(),
  // a driver on the payroll
  driver: z.boolean(),
  message: z.string().optional(),
});
export type CarsReply = z.infer<typeof CarsReply>;
export const BuyCarRequest = z.object({ model: id, tradeIn: z.boolean().optional() }).strict();
export type BuyCarRequest = z.infer<typeof BuyCarRequest>;
